import React, { useState, useEffect, Suspense, lazy } from "react";
import ResourceCalendar from "../calendar/ResourceCalendar";
import { ScheduleDataProvider, useScheduleData } from "./ScheduleDataContext";
import PreFilter from "./PreFilter";
import SelectedUsersSummary from "./SelectedUsersSummary";
import WorkRoleUserFilters from "./WorkRoleUserFilters";
const ResourceTable = lazy(() => import("./ResourceTable"));

function toDateInputValue(d) {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

export default function ScheduleTabs({ defaultTab = "calendar" }) {
  return (
    <ScheduleDataProvider>
      <TabsInner defaultTab={defaultTab} />
    </ScheduleDataProvider>
  );
}

function TabsInner({ defaultTab }) {
  const [active, setActive] = useState(defaultTab);

  useEffect(() => {
    setActive(defaultTab);
  }, [defaultTab]);
  const { refresh, loading, tokenLoading, rangeLoading, backgroundLoading, lastRefreshed, refreshVersion, prefilterReady, activeDateStart, activeDateEnd, loadDateRange } = useScheduleData();
  const busy = loading || tokenLoading;

  // Local date inputs initialised from active range
  const [startInput, setStartInput] = useState(() => toDateInputValue(activeDateStart));
  const [endInput, setEndInput] = useState(() => toDateInputValue(activeDateEnd));

  // Sync local inputs when the context's active range changes (e.g. after initial load)
  useEffect(() => {
    setStartInput(toDateInputValue(activeDateStart));
  }, [activeDateStart]);
  useEffect(() => {
    setEndInput(toDateInputValue(activeDateEnd));
  }, [activeDateEnd]);

  const rangeValid = (() => {
    const s = new Date(startInput);
    const e = new Date(endInput);
    return !isNaN(s.getTime()) && !isNaN(e.getTime()) && e > s;
  })();

  // Check if inputs differ from active range
  const rangeChanged = startInput !== toDateInputValue(activeDateStart) || endInput !== toDateInputValue(activeDateEnd);

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [roleFiltersOpen, setRoleFiltersOpen] = useState(false);

  async function handleApplyRange() {
    if (!rangeValid || busy) return;
    await loadDateRange(new Date(startInput), new Date(endInput));
  }

  if (!prefilterReady) {
    return <PreFilter />;
  }

  return (
    <div>
      {/* Collapsible date-range / filter bar */}
      <div style={{ marginBottom: 8 }}>
        <button
          onClick={() => setFiltersOpen(o => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 12px',
            background: '#263238',
            border: '1px solid #455a64',
            borderRadius: 5,
            color: '#90caf9',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            marginBottom: filtersOpen ? 8 : 0,
          }}
        >
          {filtersOpen ? '▾' : '▸'} Date Range &amp; Filters
        </button>
        {filtersOpen && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#e3f2fd' }}>
          Start
          <input
            type="date"
            value={startInput}
            onChange={(e) => setStartInput(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid #616161', background: '#263238', color: '#fff', fontSize: 13 }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#e3f2fd' }}>
          End
          <input
            type="date"
            value={endInput}
            onChange={(e) => setEndInput(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid #616161', background: '#263238', color: '#fff', fontSize: 13 }}
          />
        </label>
        <span style={{ fontSize: 11, opacity: 0.6, color: '#e3f2fd' }}>Based on resource Start Date</span>
        {rangeChanged && (
          <button
            onClick={handleApplyRange}
            disabled={!rangeValid || busy}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: rangeValid && !busy ? "2px solid #0d47a1" : "2px solid #616161",
              background: rangeValid && !busy ? "#1565c0" : "#455a64",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              cursor: rangeValid && !busy ? "pointer" : "not-allowed",
            }}
          >
            {busy ? "Loading..." : "Apply Range"}
          </button>
        )}
        {!rangeValid && startInput && endInput && (
          <span style={{ fontSize: 12, color: "#ef5350" }}>End must be after start</span>
        )}
        <button
          onClick={() => refresh()}
          disabled={busy}
          aria-label="Refresh data"
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: busy ? "2px solid #616161" : "2px solid #0d47a1",
            background: busy ? "#455a64" : "#1b5e20",
            color: "#fff",
            fontWeight: 600,
          }}
          title="Force data reload"
        >
          {busy ? "Refreshing..." : "Refresh"}
        </button>
        <span style={{ fontSize: 12, opacity: 0.6 }}>Auto-refresh every 30 min</span>
        <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
          Last updated: {lastRefreshed ? new Date(lastRefreshed).toLocaleString() : '—'}
        </span>
        {(rangeLoading || backgroundLoading) && (
          <span style={{ fontSize: 12, marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6, color: '#90caf9' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #90caf9', borderTopColor: 'transparent', animation: 'rt-spin 0.7s linear infinite' }} />
            Loading in background…
          </span>
        )}
            </div>
            <SelectedUsersSummary />
          </>
        )}
      </div>

      {/* Collapsible role & user filter section */}
      <div style={{ marginBottom: 8 }}>
        <button
          onClick={() => setRoleFiltersOpen(o => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 12px',
            background: '#263238',
            border: '1px solid #455a64',
            borderRadius: 5,
            color: '#90caf9',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            marginBottom: roleFiltersOpen ? 8 : 0,
          }}
        >
          {roleFiltersOpen ? '▾' : '▸'} Role &amp; User Filters
        </button>
        {roleFiltersOpen && <WorkRoleUserFilters />}
      </div>

      <div style={{ position: 'relative' }}>
        {active === "calendar" ? (
          <ResourceCalendar key={`cal-${refreshVersion}`} />
        ) : (
          <Suspense fallback={null}>
            <ResourceTable key={`tbl-${refreshVersion}`} />
          </Suspense>
        )}
      </div>
    </div>
  );
}
